/**
 * Hybrid Bootstrap Entry Point
 *
 * This file bootstraps Angular and AngularJS together using:
 * - @angular/upgrade for component/service interop
 * - @uirouter/angular-hybrid for shared routing
 *
 * During the migration period, both frameworks run simultaneously
 * and share the same UI-Router instance.
 *
 * Bootstrap Order:
 * 1. Zone.js is loaded (required for Angular)
 * 2. Angular bootstraps first via platformBrowserDynamic
 * 3. Angular's ngDoBootstrap() triggers AngularJS bootstrap
 * 4. UI-Router hybrid connects both routing systems
 * 5. url.listen() and url.sync() are called to start URL handling
 * 6. Both frameworks are now running together with shared routing
 */

// Import Tailwind CSS for Angular/Lit components
// All classes are prefixed with 'tw-' to avoid conflicts with Bootstrap 3
import './styles/tailwind.css';

import 'zone.js';
// IMPORTANT: Must import @angular/compiler BEFORE platformBrowserDynamic
// This is required for JIT (Just-In-Time) compilation in Angular 18
import '@angular/compiler';
import { NgZone } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { setAngularJSGlobal } from '@angular/upgrade/static';
import { UIRouter } from '@uirouter/angular';

import * as angular from 'angular';

import { AppModule } from './app/app.module';

// Required: Tell ngUpgrade which AngularJS to use
setAngularJSGlobal(angular);

/**
 * Bootstrap the hybrid application
 *
 * @param angularJsModuleName - The name of the AngularJS module to bootstrap
 */
export function bootstrapHybrid(angularJsModuleName: string): Promise<void> {
  // Set the AngularJS module name before bootstrapping
  AppModule.setAngularJsModuleName(angularJsModuleName);

  return platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .then((platformRef) => {
      const urlService = platformRef.injector.get(UIRouter).urlService;

      function startUIRouter() {
        urlService.listen();
        urlService.sync();
      }

      platformRef.injector.get<NgZone>(NgZone).run(startUIRouter);

      console.log('[Hybrid] Angular + AngularJS bootstrap complete');
      console.log('[Hybrid] UI-Router hybrid routing enabled');
    })
    .catch((err) => {
      console.error('[Hybrid] Bootstrap failed:', err);
      throw err;
    });
}

